import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Dialog,
    DialogContent,
    DialogSurface,
    DialogBody,
    DialogTitle,
    DialogActions,
    Button,
    Spinner,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { ConversationsSummaryResponse } from '../../../../model/summaries/ConversationsSummaryResponse';
import { ShiftPeriod } from '../../../../utils/shiftTimes';
import { ConversationsSummaryContent } from './ConversationsSummaryContent';
import { useActiveConversationsToolbarStyles } from './../ActiveConversationsToolbar.styles';
import { useConversationsSummaryDialogStyles } from './ConversationsSummaryDialog.styles';

interface IConversationsSummaryDialogProps {
    open: boolean;
    loading: boolean;
    error: string | null;
    summary: ConversationsSummaryResponse | null;
    selectedShift: ShiftPeriod;
    shiftOptions: Array<{ key: ShiftPeriod; label: string }>;
    onShiftChange: (_: any, data: { optionValue?: string }) => void;
    onClose: () => void;
}

export const ConversationsSummaryDialog: React.FC<
    IConversationsSummaryDialogProps
> = ({
    open,
    loading,
    error,
    summary,
    selectedShift,
    shiftOptions,
    onShiftChange,
    onClose,
}) => {
    const { t } = useTranslation();
    const toolbarStyles = useActiveConversationsToolbarStyles();
    const styles = useConversationsSummaryDialogStyles();

    const handleClose = useCallback(() => {
        onClose();
    }, [onClose]);

    return (
        <Dialog
            open={open}
            onOpenChange={(_, data) => data.open || handleClose()}
        >
            <DialogSurface
                className={
                    loading || error || !summary
                        ? toolbarStyles.dialogSurface
                        : toolbarStyles.summaryDialogSurface
                }
            >
                <DialogBody>
                    <DialogTitle
                        action={
                            <Button
                                appearance="subtle"
                                aria-label={t('Dialog_Common_Cancel')}
                                onClick={handleClose}
                                icon={<Dismiss24Regular />}
                            />
                        }
                    >
                        {t('Dialog_Summary_Title')}
                    </DialogTitle>
                    <DialogContent>
                        {loading ? (
                            <div
                                className={styles.loadingContainer}
                                role="status"
                                aria-live="polite"
                            >
                                <Spinner
                                    size="small"
                                    aria-label={t('Dialog_Summary_Loading')}
                                />
                                {t('Dialog_Summary_Loading')}
                            </div>
                        ) : error ? (
                            <div
                                style={{
                                    color: 'var(--colorStatusDangerForeground1)',
                                }}
                                role="alert"
                            >
                                {error}
                            </div>
                        ) : summary ? (
                            <ConversationsSummaryContent
                                summary={summary}
                                selectedShift={selectedShift}
                                shiftOptions={shiftOptions}
                                onShiftChange={onShiftChange}
                            />
                        ) : (
                            <div>
                                {t('Dialog_Summary_NoData', {
                                    defaultValue: 'No summary data available',
                                })}
                            </div>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button appearance="primary" onClick={handleClose}>
                            {t('Dialog_Common_Close', {
                                defaultValue: 'Close',
                            })}
                        </Button>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
};
